import React, { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { NOTICES, useAuth } from '../auth/AuthContext';
import { auth } from '../firebase';
import Logo from '../images/aren-logo.png';
import './Login.css';

function signInErrorMessage(code) {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-email':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Incorrect email or password.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your Aren administrator.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes or reset your password.';
    case 'auth/network-request-failed':
      return "Can't reach the sign-in service. Check your connection and try again.";
    case 'auth/invalid-api-key':
    case 'auth/api-key-not-valid.-please-pass-a-valid-api-key.':
    case 'auth/unauthorized-domain':
    case 'auth/operation-not-allowed':
    case 'auth/configuration-not-found':
      return "Sign-in isn't configured correctly for this site. Contact your Aren administrator.";
    default:
      return 'Sign-in failed. Please try again.';
  }
}

// An eye, struck through once the password is showing (like the phone app's
// password field).
const EyeIcon = ({ open }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
    {!open && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

const Login = () => {
  const { status, notice, signIn, resetPassword } = useAuth();
  const location = useLocation();
  const [mode, setMode] = useState('signIn'); // 'signIn' | 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'signedIn') {
    // Reopen the remembered page only for the account that was on it. Another
    // account signing in on the same browser lands on the Overview instead.
    const { from, uid } = location.state || {};
    const sameAccount = !uid || uid === auth.currentUser?.uid;
    return <Navigate to={from && sameAccount ? from : '/'} replace />;
  }

  const busy = submitting || status === 'checking';

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch (err) {
      setError(signInErrorMessage(err.code));
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await resetPassword(email);
    } catch (err) {
      // Don't reveal whether an account exists; only surface connectivity problems.
      if (err.code === 'auth/network-request-failed' || err.code === 'auth/too-many-requests') {
        setError(signInErrorMessage(err.code));
        setSubmitting(false);
        return;
      }
    }
    setResetSent(true);
    setSubmitting(false);
  };

  const switchMode = (next) => {
    setMode(next);
    setError('');
    setResetSent(false);
  };

  return (
    <div className="login-container">
      <form onSubmit={mode === 'signIn' ? handleSignIn : handleReset} className="login-form">
        <img src={Logo} alt="Aren" className="login-logo" />
        <h2 className="login-title">Aren</h2>
        <p className="login-subtitle">Health System Console</p>

        {notice && NOTICES[notice] && mode === 'signIn' && (
          <div className="notice-banner login-banner">{NOTICES[notice]}</div>
        )}

        {mode === 'reset' && !resetSent && (
          <p className="login-help">Enter your email and we'll send you a link to reset your password.</p>
        )}

        {resetSent ? (
          <div className="notice-banner login-banner">
            If an account exists for that email, a reset link is on its way.
          </div>
        ) : (
          <>
            <input
              type="email"
              placeholder="Email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="login-input"
              required
            />
            {mode === 'signIn' && (
              <div className="login-password">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  aria-label="Password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  required
                />
                <button
                  type="button"
                  className="login-eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon open={!showPassword} />
                </button>
              </div>
            )}
            {error && <p className="login-error" role="alert">{error}</p>}
            <button type="submit" className="login-button" disabled={busy}>
              {mode === 'signIn' ? (busy ? 'Logging in…' : 'Login') : (busy ? 'Sending…' : 'Send reset link')}
            </button>
          </>
        )}

        <button
          type="button"
          className="text-button login-switch"
          onClick={() => switchMode(mode === 'signIn' ? 'reset' : 'signIn')}
        >
          {mode === 'signIn' ? 'Forgot password?' : 'Back to sign in'}
        </button>
      </form>
    </div>
  );
};

export default Login;
